import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, User, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { searchMinistersForAuthority } from '@/data/territoryRepository';
import type { Minister } from '@/models/minister';

interface AuthorityAssignmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  positionLabel: string;
  currentMinister: Minister | null | undefined;
  onSave: (ministerId: string | null, startedAt: string) => Promise<void>;
  districtId?: string | null;
  regionId?: string | null;
  assignedMinisterIds?: string[];
}

function getPhotoUrl(filename: string | null): string | null {
  if (!filename) return null;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return `${supabaseUrl}/storage/v1/object/public/MinisterImages/ProfilePhotos/${filename}`;
}

export function AuthorityAssignmentDialog({
  isOpen,
  onClose,
  positionLabel,
  currentMinister,
  onSave,
  districtId,
  regionId,
  assignedMinisterIds = []
}: AuthorityAssignmentDialogProps) {
  const [search, setSearch] = useState('');
  const [ministers, setMinisters] = useState<Minister[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startedAt, setStartedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  
  // Load all ministers for the scope (no limit)
  const loadMinisters = useCallback(async (query: string) => {
    setLoading(true);
    try {
      const results = await searchMinistersForAuthority(
        query, 
        districtId, 
        regionId, 
        500 // High limit to get all ministers
      );
      setMinisters(results);
    } catch (error) {
      console.error('Error loading ministers:', error);
      setMinisters([]);
    } finally {
      setLoading(false);
    }
  }, [districtId, regionId]);
  
  // Debounced search (also handles initial load when dialog opens)
  useEffect(() => {
    if (!isOpen) return;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      loadMinisters(search);
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search, isOpen, loadMinisters]);
  
  const handleSelect = async (minister: Minister) => {
    setSaving(true);
    try {
      await onSave(minister.id, startedAt);
      onClose();
    } catch (error) {
      console.error('Error assigning authority:', error);
      alert('Error al asignar autoridad. Por favor, intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await onSave(null, startedAt);
      onClose();
    } catch (error) {
      console.error('Error clearing authority:', error);
      alert('Error al limpiar autoridad. Por favor, intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-h-[80vh] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Asignar {positionLabel}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Current assignment */}
          {currentMinister && (
            <div className="rounded-md border border-border bg-muted/50 p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">Actualmente asignado:</p>
                <button
                  onClick={handleClear}
                  disabled={saving}
                  className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
                  title="Quitar asignación"
                >
                  <Trash2 className="size-3" />
                  Quitar
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="size-10 shrink-0 overflow-hidden rounded-full bg-muted">
                  {getPhotoUrl(currentMinister.profile_picture_filename) ? (
                    <img
                      src={getPhotoUrl(currentMinister.profile_picture_filename)!}
                      alt={currentMinister.full_name || ''}
                      className="size-full object-cover"
                    />
                  ) : (
                    <div className="flex size-full items-center justify-center bg-primary/10">
                      <User className="size-5 text-primary" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {currentMinister.adb_code}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {currentMinister.full_name}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Start date */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Fecha de inicio en el cargo
            </label>
            <input
              type="date"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              disabled={saving}
            />
          </div>

          {/* Search box */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Buscar ministro {currentMinister ? '(para cambiar)' : ''}
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Escribe código o nombre..."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              disabled={saving}
            />
          </div>
          
          {/* Results list */}
          <div className="max-h-80 space-y-1 overflow-y-auto rounded-md border border-border">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : ministers.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {search ? 'No se encontraron ministros' : 'Escribe para buscar'}
              </div>
            ) : (
              ministers.map((minister) => {
                const isCurrentlyAssigned = minister.id === currentMinister?.id;
                const hasOtherPosition = !isCurrentlyAssigned && assignedMinisterIds.includes(minister.id);
                const isDisabled = saving || isCurrentlyAssigned || hasOtherPosition;
                
                return (
                  <button
                    key={minister.id}
                    onClick={() => !isDisabled && handleSelect(minister)}
                    disabled={isDisabled}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <div className="size-10 shrink-0 overflow-hidden rounded-full bg-muted">
                      <div className="flex size-full items-center justify-center bg-primary/10">
                        <User className="size-5 text-primary" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{minister.full_name}</p>
                      <p className="text-xs text-muted-foreground">{minister.adb_code}</p>
                    </div>
                    {isCurrentlyAssigned && (
                      <span className="text-xs text-muted-foreground">(actual)</span>
                    )}
                    {hasOtherPosition && (
                      <span className="text-xs text-muted-foreground">(asignado)</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
